import { StorageAdapter } from '../storage/StorageAdapter'
import { rowsOf } from '../storage/rows'
import { parseMaybeJson } from '../storage/json-codec'
import { dbgWarn } from '../logger'

/**
 * Barrido ÚNICA-VEZ al boot (TKT-2499, review Victayy). markDeadAnswerSyncOpsDone hace durable la
 * cobertura de un dead al sincronizar, pero eso solo corre cuando la occurrence VUELVE a sincronizar.
 * Un dead que YA estaba cubierto antes de este deploy (otro occurrence.syncOccurrence posterior de la
 * misma occurrence ya drenó en 'done') se queda 'dead', y si nadie vuelve a tocar esa occurrence (p.ej.
 * una auditoría finalizada) nunca se voltea -> el correo/PDF se corta con "aún sincronizando" para
 * siempre. Este barrido colapsa a 'done' esos dead ya cubiertos, usando el MISMO criterio que el
 * handler: un op de RESPUESTA en 'dead' con un occurrence.syncOccurrence 'done' de seq mayor en la misma
 * occurrence (occurrence_id vive en el body JSON -> se filtra en JS, como pendingSiblings). Los 'done'
 * que prune.js ya podó (>7 días) no se pueden recuperar; cubre la mayoría. Idempotente y marcado en meta
 * para correr una sola vez. Debe correr ANTES de startSyncWorker (antes de que el tick pode el done que
 * prueba la cobertura). Best-effort: un fallo no marca la meta y se reintenta en el próximo boot.
 */
const SWEEP_META_KEY = 'sweep:covered-dead-answers:v1'

export async function sweepCoveredDeadAnswerOps ({ db = StorageAdapter } = {}) {
  try {
    const metaRows = rowsOf(await db.query('SELECT value FROM meta WHERE key = ?', [SWEEP_META_KEY]))
    if (metaRows.length) return { skipped: true, collapsed: 0 }

    const deads = rowsOf(await db.query(
      "SELECT o.seq AS seq, a.occurrence_id AS occurrence_id " +
      "FROM outbox o JOIN answers a ON a.client_uuid = o.client_uuid " +
      "WHERE o.op = 'occurrence.syncOccurrence' AND o.status = 'dead'"
    ))

    let collapsed = 0
    if (deads.length) {
      const dones = rowsOf(await db.query(
        "SELECT seq, body FROM outbox WHERE op = 'occurrence.syncOccurrence' AND status = 'done'"
      )).map(r => ({ seq: Number(r.seq), occ: String(parseMaybeJson(r.body)?.occurrence_id) }))

      const coveredSeqs = deads
        .filter(d => dones.some(done => done.occ === String(d.occurrence_id) && done.seq > Number(d.seq)))
        .map(d => Number(d.seq))

      const now = Date.now()
      for (let i = 0; i < coveredSeqs.length; i += 200) {
        const chunk = coveredSeqs.slice(i, i + 200)
        const ph = chunk.map(() => '?').join(',')
        await db.run(
          `UPDATE outbox SET status='done', done_at=?, last_error=NULL WHERE seq IN (${ph}) AND status='dead'`,
          [now, ...chunk],
          true
        )
      }
      collapsed = coveredSeqs.length
    }

    await db.run(
      'INSERT INTO meta (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value=excluded.value',
      [SWEEP_META_KEY, '1'],
      true
    )
    return { skipped: false, collapsed }
  } catch (e) {
    dbgWarn('sync', 'sweepCoveredDeadAnswerOps falló (ignorado; se reintenta en el próximo boot)', e)
    return { error: true, collapsed: 0 }
  }
}
